import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import UserAccountsPanel from '../components/admin/UserAccountsPanel';
import {
  Link2, CheckCircle2, AlertTriangle, RefreshCw, Activity, Zap,
  TrendingUp, ExternalLink, Loader2, ChevronRight, PlayCircle, Database,
} from 'lucide-react';

const IntegrationsDashboard = () => {
  const { token } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${backendUrl}/api/integrations/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get('ghl') === 'connected') {
      setMsg('GoHighLevel connected successfully.');
      // Strip query
      navigate('/integrations', { replace: true });
    }
  }, [params, navigate]);

  // Auto-refresh every 30s for live feel
  useEffect(() => {
    const id = setInterval(() => load(), 30000);
    return () => clearInterval(id);
  }, [load]);

  const connectGhl = async () => {
    setWorking('connect');
    setErr('');
    try {
      const r = await fetch(`${backendUrl}/api/ghl/oauth/start`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed to start OAuth');
      window.location.href = data.auth_url;
    } catch (e) {
      setErr(e.message);
      setWorking('');
    }
  };

  const runE2E = async () => {
    setWorking('e2e');
    setErr('');
    setMsg('');
    try {
      const r = await fetch(`${backendUrl}/api/integrations/e2e-test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.ok) {
        setMsg(`E2E success — contact ${d.result.contact_id} (${d.result.was_created ? 'created' : 'updated'}) at location ${d.result.location_id}. Hey Derwent enrichment dispatched.`);
        setTimeout(load, 2500);
      } else {
        setErr(d.error || 'E2E failed');
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setWorking('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-page text-slate-900">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
        </div>
      </div>
    );
  }

  const ghl = data?.ghl || {};
  const leads = data?.leads || {};
  const jarvis = data?.jarvis || {};
  const scoreBuckets = jarvis.score_buckets || { '0-40': 0, '41-70': 0, '71-100': 0 };
  const totalScored = Object.values(scoreBuckets).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-page text-slate-900">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="integrations-title">
              Integrations <span className="bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">Command Centre</span>
            </h1>
            <p className="text-slate-400 text-sm">GoHighLevel · Hey Derwent · Lead Nurture — one lifecycle.</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={load}
              className="px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 hover:bg-slate-800 text-sm flex items-center gap-2"
              data-testid="integrations-refresh-btn"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              onClick={runE2E}
              disabled={!ghl.connected || working === 'e2e'}
              className="px-3 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-white text-sm flex items-center gap-2 disabled:opacity-50"
              data-testid="e2e-test-btn"
            >
              {working === 'e2e' ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
              Run E2E test
            </button>
          </div>
        </div>

        {msg && <Banner kind="ok" text={msg} />}
        {err && <Banner kind="err" text={err} />}

        {/* Connection status */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <SystemCard
            name="GoHighLevel"
            status={ghl.connected ? 'connected' : ghl.configured ? 'ready' : 'unconfigured'}
            sub={ghl.connected ? `Location ${ghl.location_id}` : ghl.configured ? 'Click connect to authorise' : 'GHL_CLIENT_ID/SECRET not set in env'}
            action={
              ghl.connected ? null : (
                <button
                  onClick={connectGhl}
                  disabled={!ghl.configured || working === 'connect'}
                  className="px-3 py-1.5 rounded-lg bg-cyan-500/15 text-cyan-200 border border-cyan-500/30 text-xs hover:bg-cyan-500/25 disabled:opacity-50"
                  data-testid="ghl-connect-btn"
                >
                  {working === 'connect' ? <Loader2 className="w-3 h-3 animate-spin inline" /> : 'Connect GHL'}
                </button>
              )
            }
            testId="system-card-ghl"
          />
          <SystemCard name="Hey Derwent (/hey-derwent)" status="connected" sub={`${jarvis.enrichments_24h || 0} enrichments in 24h`} testId="system-card-derwent" />
          <SystemCard name="Lead Nurture (/lead-nurture)" status="connected" sub={`${leads.total || 0} leads in DB`} testId="system-card-leadnurture" />
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Leads by status */}
          <Panel title="Leads by status" testId="panel-leads-by-status">
            <div className="space-y-1.5 mt-1">
              {Object.entries(leads.by_status || {}).map(([s, n]) => (
                <div key={s} className="flex items-center justify-between text-sm">
                  <span className="text-slate-300 capitalize">{s.replace(/_/g, ' ')}</span>
                  <span className="text-cyan-300 font-mono tabular-nums">{n}</span>
                </div>
              ))}
              {Object.keys(leads.by_status || {}).length === 0 && (
                <div className="text-xs text-slate-500">No leads yet.</div>
              )}
            </div>
          </Panel>

          {/* Hey Derwent score buckets */}
          <Panel title="Hey Derwent investor score (7d)" testId="panel-score-buckets">
            <div className="space-y-2 mt-2">
              {Object.entries(scoreBuckets).map(([range, n]) => {
                const pct = totalScored ? Math.round((n / totalScored) * 100) : 0;
                const color = range === '0-40' ? 'bg-rose-500' : range === '41-70' ? 'bg-amber-500' : 'bg-emerald-500';
                return (
                  <div key={range}>
                    <div className="flex items-center justify-between text-xs mb-0.5">
                      <span className="text-slate-300">{range}</span>
                      <span className="text-slate-400 font-mono">{n} · {pct}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                      <div className={`h-full ${color}`} style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>

          {/* Reliability */}
          <Panel title="Reliability (24h)" testId="panel-reliability">
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Stat icon={Zap} value={data.webhooks_24h || 0} label="Webhooks" accent="cyan" />
              <Stat icon={AlertTriangle} value={data.dlq_open || 0} label="DLQ open" accent={data.dlq_open ? 'rose' : 'emerald'} testId="dlq-open" />
              <Stat icon={Database} value={(data.recent_api_calls || []).filter(c => c.ok).length} label="API ok" accent="emerald" />
              <Stat icon={AlertTriangle} value={(data.recent_api_calls || []).filter(c => !c.ok).length} label="API err" accent="rose" />
            </div>
          </Panel>
        </div>

        {/* Recent API calls table */}
        <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4 mb-6">
          <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-400" /> Recent GHL API calls
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="api-call-table">
              <thead className="text-slate-500 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left py-1.5 pr-3">When</th>
                  <th className="text-left py-1.5 pr-3">Method</th>
                  <th className="text-left py-1.5 pr-3">Path</th>
                  <th className="text-left py-1.5 pr-3">Lead</th>
                  <th className="text-right py-1.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {(data.recent_api_calls || []).slice(0, 12).map((c, i) => (
                  <tr key={i} className="border-t border-slate-800">
                    <td className="py-1.5 pr-3 text-slate-400 font-mono">{new Date(c.ts * 1000).toLocaleTimeString()}</td>
                    <td className="py-1.5 pr-3 font-mono text-cyan-300">{c.method}</td>
                    <td className="py-1.5 pr-3 font-mono text-slate-300 truncate max-w-xs">{c.path}</td>
                    <td className="py-1.5 pr-3 text-slate-400 truncate max-w-xs">{c.lead_email || '—'}</td>
                    <td className={`py-1.5 text-right font-mono ${c.ok ? 'text-emerald-300' : 'text-rose-300'}`}>{c.status}</td>
                  </tr>
                ))}
                {(data.recent_api_calls || []).length === 0 && (
                  <tr><td colSpan={5} className="text-center py-4 text-slate-500">No API calls yet. Run an E2E test to populate.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* User Account Management (admin-only) */}
        <div className="mb-6">
          <UserAccountsPanel />
        </div>

        {/* Setup checklist */}
        {!ghl.configured && (
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
            <h3 className="text-amber-200 font-bold mb-2 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Setup required
            </h3>
            <ol className="text-sm text-slate-200 space-y-1 list-decimal list-inside">
              <li>Create a Marketplace App at <a href="https://marketplace.gohighlevel.com/" target="_blank" rel="noopener noreferrer" className="text-cyan-300 underline inline-flex items-center gap-1">GHL Marketplace <ExternalLink className="w-3 h-3" /></a> with sub-account distribution.</li>
              <li>Add scopes: <code className="text-xs">contacts.write contacts.readonly opportunities.write pipelines.readonly locations/customFields.write workflows.readonly</code></li>
              <li>Set redirect URI to <code className="text-xs text-cyan-300">https://www.derwentai.com/api/ghl/oauth/callback</code></li>
              <li>Paste <code>GHL_CLIENT_ID</code>, <code>GHL_CLIENT_SECRET</code>, and a strong random <code>GHL_WEBHOOK_SECRET</code> into the app's env vars (Emergent → Settings → Env), then redeploy.</li>
              <li>Come back and click <strong>Connect GHL</strong>.</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

const Banner = ({ kind, text }) => (
  <div className={`mb-4 px-4 py-3 rounded-xl border text-sm flex items-center gap-2 ${kind === 'ok' ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-200' : 'border-rose-500/30 bg-rose-500/5 text-rose-200'}`} data-testid={`integrations-banner-${kind}`}>
    {kind === 'ok' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} {text}
  </div>
);

const SystemCard = ({ name, status, sub, action, testId }) => {
  const styleMap = {
    connected: 'border-emerald-500/30 bg-emerald-500/5',
    ready: 'border-cyan-500/30 bg-cyan-500/5',
    unconfigured: 'border-amber-500/30 bg-amber-500/5',
  };
  const dotMap = { connected: 'bg-emerald-400', ready: 'bg-cyan-400', unconfigured: 'bg-amber-400' };
  return (
    <div className={`rounded-2xl border ${styleMap[status]} p-4`} data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`w-2 h-2 rounded-full ${dotMap[status]} animate-pulse`}></span>
            <Link2 className="w-3.5 h-3.5 text-slate-300" />
            <h3 className="text-sm font-bold text-slate-100">{name}</h3>
          </div>
          <div className="text-xs text-slate-400 ml-4">{sub}</div>
        </div>
        {action}
      </div>
    </div>
  );
};

const Panel = ({ title, children, testId }) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4" data-testid={testId}>
    <h3 className="text-[10px] uppercase tracking-[0.25em] text-slate-300 font-mono font-bold">{title}</h3>
    {children}
  </div>
);

const Stat = ({ icon: Icon, value, label, accent, testId }) => {
  const colorMap = {
    cyan: 'text-cyan-300', emerald: 'text-emerald-300', rose: 'text-rose-300', amber: 'text-amber-300',
  };
  return (
    <div className="p-2 rounded-lg bg-slate-900/40" data-testid={testId}>
      <div className="flex items-center gap-1 mb-0.5">
        <Icon className={`w-3 h-3 ${colorMap[accent]}`} />
        <span className="text-[10px] uppercase tracking-widest text-slate-400">{label}</span>
      </div>
      <div className={`text-xl font-bold font-mono tabular-nums ${colorMap[accent]}`}>{value}</div>
    </div>
  );
};

export default IntegrationsDashboard;
